import { useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import { catalogBySlug as buildCatalogBySlug,
  type UnitsPreference,
} from "@jackedlogs/shared";
import type { Exercise, Plan, Program } from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { DurableWriteFn } from "../seams";
import { FilterChipsRow } from "../ExercisePicker/FilterChipsRow";
import { EmptyState } from "../primitives/EmptyState";
import { useBreakpoint } from "../useBreakpoint";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import { LibraryItemCard } from "./LibraryItemCard";
import { buildLibraryItemCardProps } from "./buildLibraryItemCardProps";
import { LibraryFiltersPanel } from "./LibraryFilters";
import {
  applyLibraryFilters,
  collectLibraryFilterOptions,
  emptyLibraryFilters,
  type LibraryTagFilters,
} from "./libraryFilters";
import {
  filterLibraryItems,
  useLibrary,
  type LibraryFilter,
  type LibraryItem,
} from "./useLibrary";
import { useLibraryActions } from "./useLibraryActions";

// The browsable library inside the Create-hub (redesign track HR §S3):
// filter-tabs (chips acting as single-select tabs) over the composed
// program/plan buckets, grouped by source, one LibraryItemCard per row.
// Narrow = the stacked phone column; wide = a two-column card grid inside
// the hub dialog (useBreakpoint — no Platform.OS). Data + actions all come
// from useLibrary; this component is layout + grouping only.

const FILTERS: ReadonlyArray<LibraryFilter> = [
  "favorites",
  "programs",
  "workouts",
  "personal",
  "coach",
];

export type LibraryBrowserProps = {
  theme: UiTheme;
  dark: boolean;
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** Live catalog for the inline previews (muscles + exercise names).
   *  null while the host is still loading it — previews degrade to slugs. */
  catalog: Exercise[] | null;
  /** Display unit for prescribed loads in the inline plan previews.
   *  Loads are stored in kg; an lb reader must see lb here too. Default kg. */
  unit?: UnitsPreference;
  /** Personal-row management (HR-S4 — the Library owns it now that the
   *  Home lists retired): open the composer on an item. Capability-gated
   *  by omission, like every optional handler here. */
  onEditProgram?: (program: Program) => void;
  onEditPlan?: (plan: Plan) => void;
  /** Personal plans on hosts with a logger (mobile): start the session. */
  onStartPlan?: (plan: Plan) => void;
  /** Id of the plan whose start is resolving (double-tap guard). */
  startingPlanId?: string | null;
  /** Host delete-confirm seam: when provided, the host confirms (mobile
   *  Alert) and calls `run` on OK; omitted → two-tap inline on the card. */
  confirmDelete?: (item: LibraryItem, run: () => void) => void;
};

type Section = { key: string; label: string; items: LibraryItem[] };

export function LibraryBrowser({
  theme,
  dark,
  apiFetch,
  durableWrite,
  catalog,
  unit = "kg",
  onEditProgram,
  onEditPlan,
  onStartPlan,
  startingPlanId,
  confirmDelete,
}: LibraryBrowserProps) {
  const { t } = useTranslation();
  const lang = useLocale();
  const styles = makeStyles(theme);
  const { wide } = useBreakpoint();
  const resolveName = useExerciseNameResolver(catalog);
  const lib = useLibrary({ apiFetch, durableWrite });
  const [filter, setFilter] = useState<LibraryFilter>("programs");
  // Guarded management writes (follow/publish/unfollow/delete) + their
  // busy keys and error surface — extracted so this stays layout+grouping.
  const {
    actionError,
    setActionError,
    followingKey,
    mutatingKey,
    onFollow,
    onSetStatus,
    requestDelete,
  } = useLibraryActions(lib, confirmDelete);
  const [tagFilters, setTagFilters] =
    useState<LibraryTagFilters>(emptyLibraryFilters);
  const activeFilterSet = useMemo(() => new Set([filter]), [filter]);

  const catalogBySlug = useMemo(
    () => buildCatalogBySlug(catalog ?? []),
    [catalog],
  );
  // Options scoped to the ACTIVE tab, so an axis that can never match here
  // (Level on "Mine" — personal rows carry no difficulty; Sessions/week on
  // "Workouts" — plans have no frequency) simply doesn't render, instead of
  // offering chips that guarantee zero results (UX audit S3c).
  const filterOptions = useMemo(
    () =>
      collectLibraryFilterOptions(
        filterLibraryItems(lib.items, filter, lib.favoriteKeys),
        catalogBySlug,
      ),
    [lib.items, filter, lib.favoriteKeys, catalogBySlug],
  );

  const onPickFilter = (f: LibraryFilter) => {
    // Stale "Cloned X" / error banners never outlive their tab.
    lib.clearFeedback();
    setActionError(null);
    // Axis selections reset on tab switch — options are tab-scoped, so a
    // selection could otherwise keep filtering through an axis the new tab
    // doesn't even render. The typed query survives (its field stays visible).
    setTagFilters((prev) => ({ ...emptyLibraryFilters, query: prev.query }));
    setFilter(f);
  };

  const filterLabel = (f: LibraryFilter): string =>
    f === "favorites"
      ? t("library.tabFavorites", { defaultValue: "Favorites" })
      : f === "programs"
        ? t("library.tabPrograms", { defaultValue: "Programs" })
        : f === "workouts"
          ? t("library.tabWorkouts", { defaultValue: "Workouts" })
          : f === "personal"
            ? t("library.tabPersonal", { defaultValue: "Mine" })
            : t("library.tabCoach", { defaultValue: "Coach" });

  const sections = useMemo<Section[]>(() => {
    const visible = applyLibraryFilters(
      filterLibraryItems(lib.items, filter, lib.favoriteKeys),
      tagFilters,
      catalogBySlug,
      lang,
    );
    if (filter === "coach") return [];
    if (filter === "personal" || filter === "favorites") {
      return [
        {
          key: "programs",
          label: t("library.tabPrograms", { defaultValue: "Programs" }),
          items: visible.filter((i) => i.kind === "program"),
        },
        {
          key: "workouts",
          label: t("library.tabWorkouts", { defaultValue: "Workouts" }),
          items: visible.filter((i) => i.kind === "plan"),
        },
      ];
    }
    return [
      {
        key: "official",
        label: t("library.sectionOfficial", { defaultValue: "By JackedLogs" }),
        items: visible.filter((i) => i.source === "official"),
      },
      {
        key: "personal",
        label: t("library.sectionPersonal", { defaultValue: "Mine" }),
        items: visible.filter((i) => i.source === "personal"),
      },
    ];
    // `lang` included: search matches the RESOLVED name, so the visible
    // set has to recompute when the reader's language changes.
  }, [lib.items, filter, tagFilters, catalogBySlug, lib.favoriteKeys, t, lang]);

  const hasItems = sections.some((s) => s.items.length > 0);
  // Empty because the search/tag filters excluded everything (the tab has
  // data) — a different message than "you have nothing yet".
  const filteredOut =
    !hasItems &&
    filter !== "coach" &&
    filterLibraryItems(lib.items, filter, lib.favoriteKeys).length > 0;
  const loading =
    !hasItems &&
    filter !== "coach" &&
    (lib.templatesStatus === "loading" ||
      lib.personalStatus === "loading" ||
      lib.personalStatus === "idle" ||
      lib.plansStatus === "loading" ||
      lib.plansStatus === "idle" ||
      (filter === "favorites" && lib.favoritesStatus === "loading"));
  const loadFailed =
    !hasItems &&
    !loading &&
    filter !== "coach" &&
    (lib.templatesStatus === "error" ||
      lib.personalStatus === "error" ||
      lib.plansStatus === "error" ||
      (filter === "favorites" && lib.favoritesStatus === "error"));

  const onStar = async (item: LibraryItem) => {
    if (item.source !== "personal") return;
    const itemId = item.kind === "program" ? item.program.id : item.plan.id;
    const err = await lib.toggleFavorite(item.kind, itemId);
    if (err) {
      // Raw code (api_error_NNN) is a breadcrumb, not UI copy.
      console.warn("[library] favorite_failed", err.message);
      setActionError(
        t("library.errFavorite", {
          defaultValue: "Could not update favorites.",
        }),
      );
    }
  };

  const renderItem = (item: LibraryItem) => (
    <View key={item.key} style={wide ? styles.gridCell : undefined}>
      <LibraryItemCard
        {...buildLibraryItemCardProps({
          item,
          theme,
          dark,
          catalog,
          resolveName,
          unit,
          lib,
          followingKey,
          mutatingKey,
          startingPlanId,
          onFollow,
          onStar,
          onSetStatus,
          onStartPlan,
          onEditProgram,
          onEditPlan,
          requestDelete,
          confirmDelete,
        })}
      />
    </View>
  );

  return (
    <View style={styles.wrap}>
      <FilterChipsRow
        theme={theme}
        label={t("library.header", { defaultValue: "Browse" })}
        options={FILTERS}
        active={activeFilterSet}
        onToggle={onPickFilter}
        renderLabel={filterLabel}
        flush
      />

      {filter !== "coach" ? (
        <LibraryFiltersPanel
          theme={theme}
          filters={tagFilters}
          onChange={setTagFilters}
          options={filterOptions}
        />
      ) : null}

      {lib.cloneFlash ? (
        <Text style={styles.flash}>{lib.cloneFlash}</Text>
      ) : null}
      {lib.cloneError ? (
        <Text style={styles.error}>{lib.cloneError}</Text>
      ) : null}
      {actionError ? <Text style={styles.error}>{actionError}</Text> : null}

      {filter === "coach" ? (
        <EmptyState
          theme={theme}
          icon="user"
          title={t("library.coachSoonTitle", {
            defaultValue: "From your coach",
          })}
          sub={t("library.coachSoonSub", {
            defaultValue:
              "Programs and workouts your coach assigns will show up here.",
          })}
        />
      ) : loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={theme.palette.ink2} />
          <Text style={styles.muted}>
            {t("library.loading", { defaultValue: "Loading the library…" })}
          </Text>
        </View>
      ) : loadFailed ? (
        <Text style={styles.error}>
          {t("library.errLoad", {
            defaultValue: "Could not load the library.",
          })}
        </Text>
      ) : filteredOut ? (
        <EmptyState
          theme={theme}
          icon="search"
          title={t("library.noResultsTitle", { defaultValue: "No matches" })}
          sub={t("library.noResultsSub", {
            defaultValue: "Adjust the search or the filters.",
          })}
        />
      ) : !hasItems && filter === "favorites" ? (
        <EmptyState
          theme={theme}
          icon="star"
          title={t("library.emptyFavoritesTitle", {
            defaultValue: "No favorites yet",
          })}
          sub={t("library.emptyFavoritesSub", {
            defaultValue: "Star a program or workout to keep it up here.",
          })}
        />
      ) : !hasItems ? (
        <EmptyState
          theme={theme}
          title={t("library.emptyTitle", { defaultValue: "Nothing here yet" })}
          sub={t("library.emptySub", {
            defaultValue:
              "Programs and workouts you create or clone will show up here.",
          })}
        />
      ) : (
        sections.map((section) =>
          section.items.length === 0 ? null : (
            <View key={section.key} style={styles.section}>
              <Text style={styles.sectionLabel}>{section.label}</Text>
              <View style={wide ? styles.grid : styles.list}>
                {section.items.map(renderItem)}
              </View>
            </View>
          ),
        )
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    wrap: {
      gap: s.s3,
    },
    section: {
      gap: s.s2,
    },
    sectionLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    list: {
      gap: s.s3,
    },
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s3,
    },
    gridCell: {
      flexGrow: 1,
      flexBasis: "48%",
    },
    center: {
      alignItems: "center",
      gap: s.s2,
      paddingVertical: s.s5,
    },
    muted: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    // Same voice as the TemplatesShelf clone flash (brand, not a status
    // colour — it's a confirmation, not a warning).
    flash: {
      color: p.brand500,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    error: {
      color: p.danger,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
  });
}
